import { useMemo } from 'react';
import type { SelectToken, BalanceToken } from '@/types';
import { TOKEN_SYMBOL, UNIFIED_TOKENS } from '@/constants';
import { TokenBalance } from './TokenBalance';
import { UnifiedTokenBalance } from './UnifiedTokenBalance';

interface BalanceTokenListProps {
  tokens: SelectToken[] | undefined;
}

export function BalanceTokenList({ tokens }: BalanceTokenListProps) {
  const data = useMemo(() => {
    return tokens?.reduce((acc, token) => {
      const unified = UNIFIED_TOKENS.find((t) =>
        t.tokens.some(
          (t) =>
            t.token_address === token.token_address &&
            t.network === token.network
        )
      );

      if (unified) {
        const existing = acc.find((t) => t.id === unified.id);
        if (existing && 'unified' in existing) {
          existing.unified?.push(token);
        } else {
          acc.push({
            id: unified.id,
            name: unified.name,
            symbol: unified.symbol,
            icon: unified.icon,
            unified: [token],
          });
        }
      } else {
        acc.push(token);
      }

      return acc;
    }, [] as BalanceToken[]);
  }, [tokens]);

  const sorted = useMemo(() => {
    // 优先代币的顺序
    const prioritySymbols: string[] = [
      TOKEN_SYMBOL.BNB,
      TOKEN_SYMBOL.ETH,
      TOKEN_SYMBOL.SOL,
      TOKEN_SYMBOL.TON,
      TOKEN_SYMBOL.USDT,
    ];

    return data?.sort((a, b) => {
      const aIndex = prioritySymbols.indexOf(a.symbol);
      const bIndex = prioritySymbols.indexOf(b.symbol);

      // 如果都是优先代币，按优先级顺序排序
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // 如果 a 是优先代币，a 排在前面
      if (aIndex !== -1) return -1;
      // 如果 b 是优先代币，b 排在前面
      if (bIndex !== -1) return 1;

      // 都不是优先代币时，没有网络的排在前面
      if (!a.network && !b.network) return 0;
      if (!a.network) return -1;
      if (!b.network) return 1;
      // 都有网络时按字母顺序排序
      return a.network.localeCompare(b.network);
    });
  }, [data]);

  return (
    <div className="v-stack w-full card">
      {sorted?.map((token) =>
        'unified' in token ? (
          <UnifiedTokenBalance key={token.id} token={token} />
        ) : (
          <TokenBalance key={token.id} token={token} />
        )
      )}
    </div>
  );
}
